<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_products") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <!-- The "ซิงก์ไป Backoffice" button was removed on 2026-09-28: Shopee is
                         the product master now (docs/specs/2026-09-22-shopee-master-product-sync-design.md);
                         the endpoint stays for the API/ops path. -->
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="goCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("app_catalog.btn_add_product") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- #301 (shared stock §8/§11): the WMS mapping is what decides whether
                 a SKU's stock mirrors the warehouse at all — an unmapped or
                 half-mapped SKU keeps its hand-typed number and NEVER follows the
                 warehouse (spec Q3). Before `shared_mode: write` somebody has to go
                 through them, and the per-row badge in the table below only speaks
                 about the row it sits on. The number here is the LOADED PAGE's
                 shortfall (the list endpoint has no tenant-wide counter — same
                 limitation the #302 filter hint spells out), so the wording says
                 "ในหน้านี้" and the link goes to the page that counts the whole
                 catalogue. -->
            <div
                v-if="unmatchedBannerTotal > 0"
                class="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center flex-wrap"
            >
                <i class="fa fa-exclamation-triangle mr-2"></i>
                <span>{{ $t("app_catalog.stock_unmatched_banner", { n: unmatchedBannerTotal }) }}</span>
                <router-link class="ml-2 font-weight-bold" to="/seller/stock-unmatched">
                    {{ $t("app_catalog.stock_unmatched_banner_link") }}
                </router-link>
            </div>

            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col-md-5">
                            <base-input
                                v-model="params.keyword"
                                prepend-icon="fas fa-search"
                                :placeholder="$t('search')"
                                @keyup.enter.native="loadList"
                            ></base-input>
                        </div>
                        <div class="col-md-3">
                            <base-input>
                                <el-select
                                    v-model="params.app_enable"
                                    :placeholder="$t('app_catalog.col_app_enable')"
                                    clearable
                                    @change="loadList"
                                    class="w-100"
                                >
                                    <!-- "เปิดการขาย/ปิดการขาย" and NOT "เปิด/ปิดใน App"
                                         (2026-10-03 revamp): the same switch now
                                         decides whether the product can be bought
                                         at all, on every surface, so the filter has
                                         to read like the column it filters. -->
                                    <el-option :label="$t('app_catalog.sale_on')" :value="1"></el-option>
                                    <el-option :label="$t('app_catalog.sale_off')" :value="0"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <div class="col-md-2">
                            <base-button type="primary" size="sm" @click="loadList">
                                <i class="fa fa-search"></i> {{ $t("search") }}
                            </base-button>
                        </div>
                        <!-- Issue #88: flash_only filter -->
                        <div class="col-md-12 mt-2">
                            <el-checkbox v-model="params.flash_only" :true-label="1" :false-label="0" @change="loadList">
                                {{ $t("app_catalog.flash_only_filter") }}
                            </el-checkbox>
                            <!-- mdf-bo-api#583: SKU ที่ map WMS ไม่ครบ.
                                 CLIENT-SIDE, หน้านี้เท่านั้น — endpoint ไม่มี
                                 query param ให้กรอง จึงบอกไว้ข้าง ๆ ว่ากรอง
                                 เฉพาะรายการที่โหลดมาแล้ว ไม่ใช่ทั้งคลัง (ไม่
                                 บอก = คนกดจะสรุปว่าทั้งระบบสะอาดจากหน้าแรก) -->
                            <el-checkbox
                                v-model="onlyWmsUnmatched"
                                class="ml-4"
                            >
                                {{ $t("app_catalog.wms_unmatched_filter") }}
                                <small class="text-muted">({{ $t("app_catalog.wms_unmatched_filter_hint") }})</small>
                            </el-checkbox>
                            <!-- Owner rule 2026-10-15 "SKU ที่ไม่มีชื่อตัวเลือก ถือว่าไม่สมบูรณ์
                                 ห้ามเปิดขาย". SERVER-SIDE (?incomplete_sku=1, applied before
                                 count/pagination), so unlike the WMS toggle it searches the
                                 whole catalogue and needs no "this page only" hint. -->
                            <el-checkbox
                                v-model="params.incomplete_sku"
                                :true-label="1"
                                :false-label="0"
                                class="ml-4"
                                data-testid="incomplete-sku-filter"
                                @change="onIncompleteSkuFilterChange"
                            >
                                {{ $t("app_catalog.incomplete_sku_filter") }}
                            </el-checkbox>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        ref="productTable"
                        class="product-list-table"
                        :data="tableData"
                        row-key="id"
                        size="small"
                        header-row-class-name="thead-light"
                        :empty-text="$t('app_catalog.no_data')"
                        @expand-change="onExpandChange"
                    >
                        <!-- SKU drill-down (owner's screenshot, spec §5). LAZY: the
                             list endpoint carries no SKUs at all, and fetching every
                             row's detail up-front would be 20 requests for a question
                             nobody asked. One GET per row, the first time it opens,
                             cached for the life of the page. -->
                        <el-table-column type="expand">
                            <template slot-scope="{ row }">
                                <div class="px-4 py-2" v-loading="isDetailLoading(row.id)">
                                    <!-- The headerless sub-table says nothing about
                                         itself, so edit mode has to: the pencil is on
                                         the row ABOVE, and without this line the inputs
                                         look like they appeared by themselves
                                         (inline-edit spec §4). -->
                                    <div
                                        v-if="isInlineEditing(row) && skuRows(row).length > 0"
                                        class="text-muted small mb-1"
                                    >
                                        <i class="fa fa-info-circle mr-1"></i>
                                        {{ $t("app_catalog.inline_hint_edit_in_rows") }}
                                    </div>
                                    <div v-if="detailError[row.id]" class="text-danger small">
                                        {{ $t("app_catalog.expand_load_failed") }}
                                    </div>
                                    <div
                                        v-else-if="!isDetailLoading(row.id) && skuRows(row).length === 0"
                                        class="text-muted small"
                                    >
                                        {{ $t("app_catalog.expand_skus_empty") }}
                                    </div>
                                    <!-- Headerless on purpose (owner's screenshot): the
                                         sub-table is a continuation of the row above it,
                                         and a second header row reads as a second table. -->
                                    <el-table
                                        v-else
                                        :data="skuRows(row)"
                                        :show-header="false"
                                        size="mini"
                                        row-key="key"
                                    >
                                        <el-table-column width="60">
                                            <template slot-scope="sku">
                                                <img
                                                    v-if="sku.row.image"
                                                    :src="thumbUrl(sku.row.image)"
                                                    alt=""
                                                    style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px"
                                                />
                                            </template>
                                        </el-table-column>
                                        <el-table-column min-width="200px">
                                            <template slot-scope="sku">{{ sku.row.sku_name || "-" }}</template>
                                        </el-table-column>
                                        <el-table-column min-width="160px">
                                            <template slot-scope="sku">
                                                <span class="text-muted">{{ sku.row.sku_code || "-" }}</span>
                                            </template>
                                        </el-table-column>
                                        <!-- ราคา. In edit mode this is the SKU's BASE price
                                             — the only price this page may write. A Shopee
                                             flash sale owns flash_* and keeps charging its
                                             own number until it ends, so the ⚡ row says so
                                             rather than letting the operator conclude the
                                             save did nothing (spec §1 edge case 2). -->
                                        <el-table-column min-width="160px" align="right">
                                            <template slot-scope="sku">
                                                <el-input-number
                                                    v-if="isInlineEditing(row) && !isPriceFixed(row) && hasDraft(row, sku.row)"
                                                    :value="draftOf(row, sku.row).price"
                                                    :min="0.01"
                                                    :precision="2"
                                                    :controls="false"
                                                    size="mini"
                                                    style="width: 110px"
                                                    :disabled="isSkuSaving(row, sku.row)"
                                                    @input="v => setDraft(row, sku.row, 'price', v)"
                                                    @keyup.enter.native="saveSkuEdit(row, sku.row)"
                                                    @keyup.esc.native="cancelInlineEdit(row)"
                                                ></el-input-number>
                                                <span v-else>{{ formatPrice(sku.row.price) }}</span>
                                                <!-- price_mode = 'fixed': one price for the
                                                     whole product, so a per-SKU input would
                                                     offer a write the BE answers with 409. -->
                                                <div
                                                    v-if="isInlineEditing(row) && isPriceFixed(row)"
                                                    class="text-muted small"
                                                >
                                                    {{ $t("app_catalog.inline_price_fixed_hint") }}
                                                </div>
                                                <div
                                                    v-if="isInlineEditing(row) && sku.row.flash_source === 'shopee'"
                                                    class="text-danger small"
                                                >
                                                    <i class="fa fa-bolt mr-1"></i>
                                                    {{ $t("app_catalog.inline_price_flash_hint") }}
                                                </div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column min-width="170px" align="right">
                                            <template slot-scope="sku">
                                                <!-- A WMS-owned quantity NEVER gets an input,
                                                     in edit mode or out of it: shared stock
                                                     has been in write mode since 2026-10-03
                                                     11:33, so anything typed here is gone
                                                     within 20 s and the BE refuses it with
                                                     409 quantity_wms_owned. Read-only number
                                                     + "จาก WMS", exactly as before. -->
                                                <el-input-number
                                                    v-if="isInlineEditing(row) && canEditSkuQty(sku.row) && hasDraft(row, sku.row)"
                                                    :value="draftOf(row, sku.row).quantity"
                                                    :min="0"
                                                    :precision="0"
                                                    size="mini"
                                                    style="width: 120px"
                                                    :disabled="isSkuSaving(row, sku.row)"
                                                    @input="v => setDraft(row, sku.row, 'quantity', v)"
                                                    @keyup.enter.native="saveSkuEdit(row, sku.row)"
                                                    @keyup.esc.native="cancelInlineEdit(row)"
                                                ></el-input-number>
                                                <template v-else>
                                                    <!-- The sub-table is headerless, so the
                                                         one ambiguous number carries its
                                                         label as a hover title. -->
                                                    <span :title="$t('app_catalog.col_available_qty')">
                                                        {{ sku.row.quantity }}
                                                    </span>
                                                    <!-- #301: whose number this is. A WMS-owned
                                                         quantity cannot be edited in the seller
                                                         module at all, so saying nothing here
                                                         invites staff to go look for the field. -->
                                                    <small v-if="sku.row.fromWms" class="text-muted ml-1">
                                                        <!-- Owner feedback 2026-10-03: in edit
                                                             mode the row next to an editable one
                                                             has to say why it is NOT a field.
                                                             The padlock reads as "locked", and
                                                             its title names the two places the
                                                             number can actually be changed. -->
                                                        <i
                                                            v-if="isInlineEditing(row)"
                                                            class="fa fa-lock mr-1"
                                                            :title="$t('app_catalog.inline_qty_from_wms_hint')"
                                                        ></i>
                                                        {{ $t("app_catalog.sku_quantity_from_wms") }}
                                                    </small>
                                                </template>
                                            </template>
                                        </el-table-column>
                                        <!-- ✓ / ✗ per SKU row: one PATCH per row, never a
                                             bulk save (spec §7). The column only exists in
                                             edit mode so the read-only table keeps its
                                             width. -->
                                        <el-table-column v-if="isInlineEditing(row)" width="100" align="center">
                                            <template slot-scope="sku">
                                                <base-button
                                                    size="sm"
                                                    type="primary"
                                                    class="btn-icon-only"
                                                    :disabled="isSkuSaving(row, sku.row)"
                                                    :title="$t('app_catalog.inline_save')"
                                                    @click="saveSkuEdit(row, sku.row)"
                                                >
                                                    <i class="fa fa-check"></i>
                                                </base-button>
                                                <base-button
                                                    size="sm"
                                                    type="secondary"
                                                    class="btn-icon-only ml-1"
                                                    :disabled="isSkuSaving(row, sku.row)"
                                                    :title="$t('app_catalog.inline_cancel')"
                                                    @click="cancelInlineEdit(row)"
                                                >
                                                    <i class="fa fa-times"></i>
                                                </base-button>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </div>
                            </template>
                        </el-table-column>
                        <!-- รูปภาพ in place of the ID column (owner 2026-10-15: "ตัดคอลัม ID
                             ออกแล้วเปลี่ยนเป็นรูปภาพแทน"). `image_url` is the list row's
                             cover image (mdf-bo-api #357, first gallery image, already
                             resolved/signed by the BE) — it goes through the same
                             cdnImageUrl rule as the SKU thumbnails below; "" means no
                             picture, and the row shows a neutral placeholder instead of
                             a broken <img>. Fixed `width`: el-table hands spare width to
                             every min-width column equally, and this one must not grow.
                             COLUMN BUDGET (render-measured 2026-10-15): the card is 1056px
                             wide at 1366 with the sidebar pinned (1130 at 1440). Every
                             column except ชื่อ (ไทย) is a fixed `width` with
                             `product-list-tight` (8px side padding instead of the theme's
                             1.5rem, see the style block), 784px together, so the name
                             gets ~266px at 1366 / ~340px at 1440 (was 200 / 204) and
                             nothing scrolls sideways. สถานะการขาย is 148 because the en
                             badge "⚠ WMS incomplete 3" is 130px wide; the kebab column is
                             72 so the en header "ACTION" stays on one line. Widen a
                             fixed column = take it from the name. -->
                        <el-table-column :label="$t('app_catalog.col_image')" width="64" align="center" class-name="product-list-tight" label-class-name="header-cell-center">
                            <template slot-scope="{ row }">
                                <!-- Owner 2026-10-15: the picture opens the large-image
                                     lightbox (bottom of this template) for everyone who
                                     can see the list; the editor is on the name. A real
                                     <button> so it is reachable by Tab and Enter/Space. -->
                                <button
                                    v-if="thumbUrl(row.image_url)"
                                    type="button"
                                    class="product-list-thumb-btn"
                                    data-testid="product-thumb"
                                    :aria-label="$t('app_catalog.image_preview_open', { name: row.name_th || '' })"
                                    @click.stop="openImagePreview(row, $event)"
                                >
                                    <img
                                        :src="thumbUrl(row.image_url)"
                                        :alt="row.name_th || ''"
                                        loading="lazy"
                                        class="product-list-thumb"
                                    />
                                </button>
                                <!-- No picture: nothing to enlarge, so not clickable. -->
                                <span
                                    v-else
                                    class="product-list-thumb product-list-thumb--empty"
                                    data-testid="product-thumb-empty"
                                >
                                    <i class="fa fa-image"></i>
                                </span>
                            </template>
                        </el-table-column>
                        <!-- ชื่อ (ไทย) is the ONLY min-width column left in the row, so it
                             takes every pixel the ID and WMS columns gave back (owner
                             2026-10-15: "ขยายชื่อไทยให้กว้างขึ้น"). Clamped to two lines;
                             the full name is on the title attribute. -->
                        <el-table-column :label="$t('app_catalog.col_name_th')" min-width="240px">
                            <template slot-scope="{ row }">
                                <!-- ⚡ before the name instead of the old flash COLUMN
                                     (spec §5): the column cost 80px on every row to
                                     say "no" about almost all of them. -->
                                <span class="product-list-name" :title="row.name_th">
                                    <el-tooltip
                                        v-if="flashActive(row)"
                                        :content="$t('app_catalog.flash_active_tooltip')"
                                        placement="top"
                                    >
                                        <i class="fa fa-bolt text-danger mr-1"></i>
                                    </el-tooltip>
                                    <!-- Owner 2026-10-15: the name opens the editor — the
                                         same route and the same gate (isAdmin) as the
                                         kebab's "แก้ไข"; without that gate it stays text. -->
                                    <router-link
                                        v-if="isAdmin && editPath(row.id)"
                                        :to="editPath(row.id)"
                                        class="product-list-name-link"
                                        data-testid="product-name-link"
                                    >{{ row.name_th }}</router-link>
                                    <template v-else>{{ row.name_th }}</template>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_code')" prop="code" width="120" class-name="product-list-tight"></el-table-column>
                        <!-- ราคา + ✎ (inline-edit spec §4). The RANGE and not the
                             product's own `price`: a product with per-SKU prices has no
                             single price, and the one column was reporting whichever
                             number the editor last wrote at the product level. Comes
                             from the expanded detail when it is cached (so the row
                             agrees with the sub-table the operator just saved in),
                             otherwise from the list's price_min/price_max, otherwise
                             from `price` for an API older than BE-2. -->
                        <el-table-column :label="$t('app_catalog.col_price')" width="156" class-name="product-list-tight">
                            <template slot-scope="{ row }">
                                {{ rowPriceRangeOf(row).text }}
                                <!-- @click.native.stop: the pencil sits inside the row,
                                     and without .stop element-ui would ALSO toggle the
                                     expansion the handler is about to open — i.e. a
                                     click would open and close it again. -->
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="link"
                                    class="btn-icon-only ml-1"
                                    :title="$t('app_catalog.inline_edit_price')"
                                    @click.native.stop="startInlineEdit(row, 'price')"
                                >
                                    <i class="fa fa-pencil-alt"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                        <!-- จำนวนสินค้าพร้อมขาย: the number the STOREFRONT can sell,
                             summed over live SKUs by the BE — not the sum of the
                             quantity column, which includes inactive SKUs. Both
                             pencils lead to the same place (startInlineEdit), because
                             price and quantity are edited in the same sub-table. -->
                        <el-table-column :label="$t('app_catalog.col_sellable_qty')" width="110" class-name="product-list-tight">
                            <!-- Two lines on purpose (owner/coordinator 2026-10-15): in 94px of
                                 content "จำนวนสินค้าพร้อมขาย" has to wrap, and left to the browser it
                                 broke inside a word ("…พร้อมข / าย", element-ui's
                                 word-break: break-all) or, with dictionary breaking, between
                                 "พร้อม" and "ขาย". col_sellable_qty_header carries the break
                                 ("\n", rendered by white-space: pre-line); `label` stays the
                                 one-line text. -->
                            <template slot="header">
                                <span class="product-list-head-lines">{{ $t("app_catalog.col_sellable_qty_header") }}</span>
                            </template>
                            <template slot-scope="{ row }">
                                {{ formatQty(row.sellable_qty) }}
                                <!-- Owner feedback, bo-stg 2026-10-03: on a product whose
                                     SKUs are ALL warehouse-owned this pencil opened edit
                                     mode with every qty cell read-only. It is withheld
                                     once we know that (i.e. once the detail is cached —
                                     the list row carries no per-SKU stock_source); until
                                     then it is shown and the handler answers in a toast,
                                     because hiding a control on a guess is worse. -->
                                <base-button
                                    v-if="isAdmin && showQtyPencil(row)"
                                    size="sm"
                                    type="link"
                                    class="btn-icon-only ml-1"
                                    :title="$t('app_catalog.inline_edit_qty')"
                                    @click.native.stop="startInlineEdit(row, 'quantity')"
                                >
                                    <i class="fa fa-pencil-alt"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                        <!-- The Amaze sync column (#263) was REMOVED on 2026-10-03
                             (spec §5): it spoke about one optional marketplace and
                             took 170px from the two questions the seller actually
                             opens this page with — can it be sold, and is it on.
                             @/utils/amazePush and the push modal stay on the product
                             EDITOR, which is where the links are read now; this list
                             no longer asks for them. -->
                        <!-- สถานะการขาย (spec §4). NOT the same question as the switch
                             next to it: a product can be switched ON and still be
                             unsellable because every SKU is at 0, which is exactly
                             the case the old "เปิดใน App" tag reported as green. The
                             yellow badge always carries the reason in its tooltip. -->
                        <!-- WMS ไม่ครบ lives HERE since 2026-10-15 (owner: "กรณี wms ไม่ครบ
                             ให้แสดงในสถานะการขาย ตัดคอลัม wms ออก"). It is mdf-bo-api#583's
                             half-mapping warning: a SKU whose combo code asks for more
                             warehouse codes than its wms_map holds. `wms_mapped` is TRUE
                             for a half mapping, so without this line the product looks
                             ready while the packing room picks one item of two. Same
                             badge, same count and same tooltip the old WMS column had
                             (the tooltip names the missing codes when the API can say
                             which they are — see @/utils/wmsCoverage); nothing on a row
                             with nothing short. -->
                        <el-table-column :label="$t('app_catalog.col_sale_status')" width="148" class-name="product-list-tight">
                            <template slot-scope="{ row }">
                                <el-tooltip
                                    v-if="saleStatus(row).hintKey"
                                    :content="$t(saleStatus(row).hintKey)"
                                    placement="top"
                                >
                                    <el-tag :type="saleStatus(row).type" size="small">
                                        {{ $t(saleStatus(row).labelKey) }}
                                    </el-tag>
                                </el-tooltip>
                                <el-tag v-else :type="saleStatus(row).type" size="small">
                                    {{ $t(saleStatus(row).labelKey) }}
                                </el-tag>
                                <div v-if="wmsUnmatchedCountOf(row) > 0" class="product-list-wms">
                                    <el-tooltip
                                        :content="wmsUnmatchedTooltipOf(row)"
                                        placement="top"
                                        popper-class="product-list-wms-tip"
                                    >
                                        <el-tag type="warning" size="mini" effect="plain">
                                            <i class="fa fa-exclamation-triangle mr-1"></i>{{ $t("app_catalog.wms_unmatched_badge") }} {{ wmsUnmatchedCountOf(row) }}
                                        </el-tag>
                                    </el-tooltip>
                                </div>
                                <!-- Owner rule 2026-10-15: a live SKU with a blank option
                                     name makes the product INCOMPLETE — the storefront
                                     hides that SKU and the API refuses to put the product
                                     on sale. `incomplete_sku_count` comes with every list
                                     row (bo-api feat/block-incomplete-sku); an older API
                                     leaves it out and the row shows nothing. Same badge
                                     style as WMS ไม่ครบ above. -->
                                <div
                                    v-if="incompleteSkuCountOf(row) > 0"
                                    class="product-list-wms product-list-incomplete"
                                    data-testid="incomplete-sku-badge"
                                >
                                    <el-tooltip
                                        :content="$t('app_catalog.incomplete_sku_tooltip', { n: incompleteSkuCountOf(row) })"
                                        placement="top"
                                    >
                                        <el-tag type="warning" size="mini" effect="plain">
                                            <i class="fa fa-exclamation-triangle mr-1"></i><template
                                                v-for="(line, i) in incompleteBadgeLines()"
                                            ><template v-if="i > 0">{{ " " }}</template><span :key="i" class="product-list-nowrap">{{ line }}</span></template>
                                        </el-tag>
                                    </el-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                        <!-- เปิด-ปิด: optimistic, no confirm (spec §5). The switch is
                             a PATCH of one column (app-enable) and NOT the editor's
                             PUT — a partial PUT would soft-delete every SKU missing
                             from the payload (mdf-bo-api controllers/Catalog_product.go:839).
                             Disabled while its own request is in flight so a double
                             click cannot send two opposite writes. -->
                        <el-table-column :label="$t('app_catalog.col_toggle')" width="72" class-name="product-list-tight">
                            <template slot-scope="{ row }">
                                <el-switch
                                    :value="row.app_enable"
                                    :active-value="1"
                                    :inactive-value="0"
                                    :disabled="!isAdmin || !!row.$toggling"
                                    @change="v => onToggleAppEnable(row, v)"
                                ></el-switch>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="isAdmin" :label="$t('actions')" width="72" align="center" class-name="product-list-tight" label-class-name="header-cell-center">
                            <template slot-scope="{ row }">
                                <!-- One kebab instead of two coloured buttons
                                     (pattern: src/views/ModernTrade/MtPriceTemplates.vue).
                                     @click.native.stop keeps the trigger from toggling
                                     the row's expand state. -->
                                <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        class="btn-icon-only"
                                        :title="$t('actions')"
                                        @click.native.stop
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="edit">
                                            <i class="fa fa-edit text-info mr-2"></i>{{ $t("app_catalog.edit_product") }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="preview" :disabled="!previewUrl(row)">
                                            <i class="fa fa-external-link-alt text-primary mr-2"></i>{{ $t("app_catalog.action_preview") }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" divided>
                                            <span class="text-danger">
                                                <i class="fa fa-trash mr-2"></i>{{ $t("app_catalog.btn_delete") }}
                                            </span>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <template slot="footer">
                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="total"
                            :page-size="params.limit"
                            :current-page="params.page"
                            @current-change="onPageChange"
                        ></el-pagination>
                    </div>
                </template>
            </card>
        </div>

        <!-- Large-image preview (owner 2026-10-15): the shared component
             (components/ImagePreview/ImagePreviewLightbox.vue, bo-fn #475), the
             same lightbox as the /seller/orders inline order detail. It portals
             itself to <body>, closes on ×, Esc and the backdrop, traps Tab on ×,
             returns focus to the thumbnail and unlocks the body scroll on close,
             keep-alive deactivation and destroy. -->
        <image-preview-lightbox :preview="imagePreview" @close="imagePreview = null" />
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import refreshOnArrival from "@/mixins/refreshOnArrival";
import { consumeRefresh } from "@/utils/pageRefresh";
import {
    Table,
    TableColumn,
    Select,
    Option,
    Tag,
    Pagination,
    Checkbox,
    Tooltip,
    Switch,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    InputNumber
} from "element-ui";
import {
    wmsUnmatchedCount,
    wmsUnmatchedTooltip,
    filterWmsUnmatched
} from "@/utils/wmsCoverage";
// #301 — the banner's number. Pure + unit-tested in tests/unit/stockSource.spec.js.
import { unmatchedBannerCount } from "@/utils/stockSource";
// CDN image URLs are signed; the resolve rule is pure + unit-tested in
// tests/unit/cdnImage.spec.js. See @/utils/cdnImage for why nothing here may
// compose a CDN URL out of a bare object key any more.
import { cdnImageUrl } from "@/utils/cdnImage";
// 2026-10-03 revamp — the badge rule, the ⚡ rule and the drill-down shape.
// Pure + unit-tested in tests/unit/sellerProductRow.spec.js.
// The inline price/qty edit rules (docs/specs/2026-10-03-seller-inline-price-qty-edit-design.md)
// live in the same module, for the same reason: a .vue file cannot be mounted here.
import {
    saleStatusOf,
    hasActiveFlash,
    skuRowsOf,
    storefrontProductUrl,
    rowPriceRange,
    canEditQty,
    allSkusFromWms,
    skuEditPayload,
    inlineEditErrorKey,
    productEditPath,
    imagePreviewOf
} from "@/utils/sellerProductRow";
import ImagePreviewLightbox from "@/components/ImagePreview/ImagePreviewLightbox.vue";
import { previewOf } from "@/utils/imagePreview";
// Owner rule 2026-10-15 (blank option name = incomplete). Pure + unit-tested in
// tests/unit/skuIncomplete.spec.js.
import {
    incompleteSkuCount,
    isOptionNameRequiredError,
    withIncompleteSkuFilter,
    badgeLines,
    SKU_NAME_REQUIRED_KEY
} from "@/utils/skuIncomplete";

export default {
    name: "AppCatalogProducts",
    mixins: [refreshOnArrival],
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination,
        [Checkbox.name]: Checkbox,
        [Tooltip.name]: Tooltip,
        [Switch.name]: Switch,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [InputNumber.name]: InputNumber,
        ImagePreviewLightbox
    },
    data() {
        return {
            params: {
                page: 1,
                limit: 20,
                keyword: "",
                app_enable: null,
                flash_only: 0,
                // Owner rule 2026-10-15 — 1 = only products with a blank-name SKU.
                // Sent as incomplete_sku=1 only when on (withIncompleteSkuFilter).
                incomplete_sku: 0
                // `with_platform_links: 1` (#263) was dropped on 2026-10-03 with
                // the Amaze badge column: nothing on this page renders a platform
                // link any more, and the flag makes the BE pre-read every row's
                // links for a column that no longer exists. The product EDITOR
                // still reads links, per product, where they are shown.
            },
            // mdf-bo-api#583: show only the rows whose wms_map is incomplete. NOT
            // part of `params` — it is not sent to the API (there is no server-side
            // filter for it) and putting it there would suggest otherwise.
            onlyWmsUnmatched: false,
            // ---- 2026-10-03 revamp: the SKU drill-down cache ----
            // Keyed by product id. Kept in the component and NOT in vuex: the
            // module's `current` holds ONE product (the editor's), and routing
            // twenty expanded rows through it would make every row show the last
            // one fetched.
            detailById: {},
            detailLoading: {},
            detailError: {},
            // ---- inline price / quantity edit (spec 2026-10-03, §4) ----
            // All three keyed by PRODUCT id, because edit mode belongs to one
            // expanded row and several rows can be open at once.
            //   inlineEdit  {id: {field}}            — which pencil opened it
            //   skuDraft    {id: {skuKey: {price, quantity}}}
            //   skuSaving   {id: {skuKey: true}}     — a PATCH in flight
            // Drafts are SEEDED when edit mode opens and are never read through
            // a side-effecting getter: creating them during render would mutate
            // state inside a render pass.
            inlineEdit: {},
            skuDraft: {},
            skuSaving: {},
            // ---- large-image preview (owner 2026-10-15) ----
            // ImagePreviewLightbox's `preview` prop ({src, caption, trigger}),
            // null = closed.
            imagePreview: null
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogProduct/list",
            total: "AppCatalogProduct/total",
            loading: "AppCatalogProduct/loading",
            isAdmin: "Auth/isAdmin"
        }),
        // The per-product "WMS mapped" badge was removed 2026-09-12 (owner):
        // SKU→WMS mapping is resolved when orders are synced into bo_orders,
        // so the list no longer reports it. The BE field `wms_mapped` is ignored.
        // `wms_unmatched_count` is a different question — see the column.
        //
        // The pager still counts the FULL page set (`total` comes from the API), so
        // the toggle narrows what is rendered and deliberately does not pretend to
        // re-paginate; the label says "this page only".
        tableData() {
            return filterWmsUnmatched(this.list, this.onlyWmsUnmatched);
        },
        // #301 banner: counted over `list` (the whole loaded page) and NOT over
        // `tableData` — ticking "เฉพาะที่ WMS ไม่ครบ" must not change the number the
        // banner reports, and a banner that read the filtered rows would say 0 the
        // moment someone filtered them out.
        unmatchedBannerTotal() {
            return unmatchedBannerCount(this.list);
        }
    },
    mounted() {
        // Fresh mount is already fresh: drop a stale flag so the next activation does not refetch.
        consumeRefresh(this.$route.fullPath);
        this.loadList();
    },
    methods: {
        // refreshOnArrival mixin: a sender asked for a refresh before navigating here.
        reloadOnArrival() {
            this.loadList();
        },
        ...mapActions({
            fetchList: "AppCatalogProduct/fetchList",
            removeItem: "AppCatalogProduct/remove",
            fetchDetail: "AppCatalogProduct/fetchDetail",
            setAppEnable: "AppCatalogProduct/setAppEnable",
            patchSku: "AppCatalogProduct/patchSku"
        }),
        loadList() {
            this.fetchList(withIncompleteSkuFilter(this.params));
        },
        // A server-side filter changes `total`, so page 3 of the old set may not
        // exist in the new one — start from page 1.
        onIncompleteSkuFilterChange() {
            this.params.page = 1;
            this.loadList();
        },
        incompleteSkuCountOf(row) {
            return incompleteSkuCount(row);
        },
        incompleteBadgeLines() {
            return badgeLines(this.$t("app_catalog.incomplete_sku_badge"));
        },
        // ---- 2026-10-03 revamp: สถานะการขาย + ⚡ + เปิด-ปิด ----
        // Every rule is in @/utils/sellerProductRow; these are one-liners on
        // purpose so the page cannot hold an opinion of its own about them.
        saleStatus(row) {
            return saleStatusOf(row);
        },
        flashActive(row) {
            return hasActiveFlash(row);
        },
        previewUrl(row) {
            return storefrontProductUrl(row && row.id);
        },
        // Optimistic write (spec §5): the switch moves first, the request follows,
        // and a failure puts it back. The list is NOT reloaded on either path —
        // a reload would throw away the operator's filters and scroll position to
        // re-fetch twenty rows for a change to one column.
        async onToggleAppEnable(row, value) {
            if (!this.isAdmin || row.$toggling) return;
            const previous = row.app_enable;
            const next = Number(value) === 1 ? 1 : 0;
            this.$set(row, "app_enable", next);
            this.$set(row, "$toggling", true);
            try {
                const res = await this.setAppEnable({ id: row.id, app_enable: next });
                const body = (res && res.data && res.data.data) || (res && res.data) || {};
                // The response is the source of truth for the badge: switching a
                // product on does not create stock, so the badge may legitimately
                // stay yellow — and it must say so without a page reload.
                if (body.app_enable !== undefined && body.app_enable !== null) {
                    this.$set(row, "app_enable", Number(body.app_enable) === 1 ? 1 : 0);
                }
                if (body.sellable_qty !== undefined && body.sellable_qty !== null) {
                    this.$set(row, "sellable_qty", body.sellable_qty);
                }
                this.$notify({
                    type: "success",
                    message: this.$t(next === 1 ? "app_catalog.toggle_on_success" : "app_catalog.toggle_off_success")
                });
            } catch (err) {
                this.$set(row, "app_enable", previous);
                // Owner rule 2026-10-15: switching ON a product with a blank-name SKU
                // is refused (400 sku_name_required) — say why, not just "failed".
                this.$notify({
                    type: "danger",
                    message: this.$t(isOptionNameRequiredError(err) ? SKU_NAME_REQUIRED_KEY : "app_catalog.toggle_failed")
                });
            } finally {
                this.$set(row, "$toggling", false);
            }
        },
        // ---- the SKU drill-down ----
        isDetailLoading(id) {
            return !!this.detailLoading[id];
        },
        skuRows(row) {
            return skuRowsOf(this.detailById[row && row.id]);
        },
        // element-ui sends (row, expandedRows) for a type="expand" column and
        // (row, expanded) in some builds — both are handled, because guessing
        // wrong here means the fetch never fires.
        onExpandChange(row, expanded) {
            const isOpen = Array.isArray(expanded)
                ? expanded.some(r => r && r.id === row.id)
                : !!expanded;
            if (!isOpen) {
                // Collapsing is a cancel: a draft nobody can see is a draft the
                // operator will believe was saved when they reopen the row.
                this.cancelInlineEdit(row);
                return;
            }
            this.loadDetail(row.id);
        },
        async loadDetail(id) {
            if (!id) return;
            // Once per id for the life of the page: re-fetching on every open
            // would make the chevron a load button.
            if (this.detailById[id] || this.detailLoading[id]) return;
            this.$set(this.detailLoading, id, true);
            this.$set(this.detailError, id, false);
            try {
                const detail = await this.fetchDetail(id);
                this.$set(this.detailById, id, detail || {});
            } catch (err) {
                this.$set(this.detailError, id, true);
            } finally {
                this.$set(this.detailLoading, id, false);
            }
        },
        // ---- inline price / quantity edit (spec 2026-10-03) ----
        //
        // ราคา on the collapsed row: the RANGE over the SKUs. Reads the cached
        // detail first so the row and the sub-table cannot disagree after a save.
        rowPriceRangeOf(row) {
            const detail = this.detailById[row && row.id];
            return rowPriceRange(row, detail && detail.skus);
        },
        // Thousands separators, no decimals — and "-" when the API said nothing,
        // because `0` is a claim ("nothing to sell") that an un-migrated list
        // endpoint has not made.
        formatQty(v) {
            if (v === null || v === undefined || v === "") return "-";
            const n = Number(v);
            if (!isFinite(n)) return "-";
            return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
        },
        isInlineEditing(row) {
            return !!this.inlineEdit[row && row.id];
        },
        // Both pencils land here — there is ONE edit mode, not a price mode and a
        // quantity mode, because both numbers live in the same sub-table and
        // hiding one of them would make the other pencil look broken. `field`
        // is kept only to say which one asked.
        async startInlineEdit(row, field) {
            if (!this.isAdmin || !row || !row.id) return;
            const table = this.$refs.productTable;
            if (table && typeof table.toggleRowExpansion === "function") {
                table.toggleRowExpansion(row, true);
            }
            // The pencil may be the first thing that ever opened this row, so the
            // detail (and therefore the SKU list) may not exist yet. Seed the drafts
            // from it BEFORE edit mode turns on: el-input-number clamps a null
            // value to its :min (0.01) and emits that back through @input, and a
            // draft written that way looked like typing to seedDrafts, which then
            // kept it (bug seen on bo-stg 2026-10-03: every price showed 0.01).
            await this.loadDetail(row.id);
            // Owner feedback, bo-stg 2026-10-03: the quantity pencil on an
            // all-WMS product used to open edit mode in which every qty cell was
            // read-only. ONE handler, with the branch inside it (both pencils
            // must keep sharing this entry point): the row stays EXPANDED — so
            // the operator can see the จาก WMS rows for themselves — and the
            // toast names where the number can be changed. Nothing enters edit
            // mode, and the pencil is gone next render because the detail that
            // answers the question is now cached.
            if (field === "quantity" && allSkusFromWms(this.skuRows(row))) {
                this.$notify({ type: "info", message: this.$t("app_catalog.inline_qty_all_wms") });
                return;
            }
            this.seedDrafts(row);
            this.$set(this.inlineEdit, row.id, { field: field });
        },
        // The qty pencil is withheld only when the answer is KNOWN: the list row
        // carries no per-SKU stock_source, so an un-expanded row keeps its pencil
        // and startInlineEdit does the explaining. Price is never withheld — a
        // WMS SKU's price is still the seller's.
        showQtyPencil(row) {
            if (!this.detailById[row && row.id]) return true;
            return !allSkusFromWms(this.skuRows(row));
        },
        // One draft per SKU, from the CURRENT values. Existing drafts are kept, so
        // re-clicking a pencil while editing does not throw away typing.
        seedDrafts(row) {
            if (!row || !row.id) return;
            if (!this.skuDraft[row.id]) this.$set(this.skuDraft, row.id, {});
            const drafts = this.skuDraft[row.id];
            const rows = this.skuRows(row);
            for (let i = 0; i < rows.length; i++) {
                const sku = rows[i];
                if (drafts[sku.key]) continue;
                this.$set(drafts, sku.key, {
                    price: sku.price,
                    // A WMS row has no input; seeding it anyway keeps the shape
                    // uniform, and skuEditPayload drops it regardless.
                    quantity: sku.quantity
                });
            }
        },
        // A seeded draft exists for this SKU. The inputs render only when it does:
        // an input bound to the fallback object below would clamp null → :min and
        // write that clamp back as if the operator had typed it.
        hasDraft(row, skuRow) {
            const byProduct = this.skuDraft[row && row.id] || {};
            return !!byProduct[skuRow && skuRow.key];
        },
        // READ ONLY (no $set): called from render. The fallback object is for the
        // one frame between "edit mode on" and "detail resolved".
        draftOf(row, skuRow) {
            const byProduct = this.skuDraft[row && row.id] || {};
            return byProduct[skuRow && skuRow.key] || { price: null, quantity: null };
        },
        setDraft(row, skuRow, field, value) {
            if (!row || !row.id || !skuRow || !skuRow.key) return;
            if (!this.skuDraft[row.id]) this.$set(this.skuDraft, row.id, {});
            const drafts = this.skuDraft[row.id];
            if (!drafts[skuRow.key]) {
                this.$set(drafts, skuRow.key, { price: skuRow.price, quantity: skuRow.quantity });
            }
            this.$set(drafts[skuRow.key], field, value);
        },
        cancelInlineEdit(row) {
            const id = row && row.id;
            if (!id) return;
            this.$delete(this.inlineEdit, id);
            this.$delete(this.skuDraft, id);
            this.$delete(this.skuSaving, id);
        },
        isSkuSaving(row, skuRow) {
            const byProduct = this.skuSaving[row && row.id] || {};
            return !!byProduct[skuRow && skuRow.key];
        },
        setSkuSaving(row, skuRow, val) {
            if (!row || !row.id || !skuRow || !skuRow.key) return;
            if (!this.skuSaving[row.id]) this.$set(this.skuSaving, row.id, {});
            this.$set(this.skuSaving[row.id], skuRow.key, !!val);
        },
        // The rule lives in @/utils/sellerProductRow, where it is unit-tested: a
        // WMS-owned quantity gets no input, ever.
        canEditSkuQty(skuRow) {
            return canEditQty(skuRow);
        },
        // One price for every SKU ⇒ the per-SKU price input would offer a write
        // the BE answers with 409 price_mode_fixed. The flag is on the DETAIL;
        // the list row is checked too in case a later BE puts it there.
        isPriceFixed(row) {
            const detail = this.detailById[row && row.id] || {};
            const mode = detail.price_mode || (row && row.price_mode) || "";
            return String(mode) === "fixed";
        },
        /**
         * Save ONE SKU row. Deliberately NOT optimistic (spec §4): this is money
         * and stock, nothing in the table moves before the PATCH resolves, and
         * everything that moves afterwards is copied from the RESPONSE — which
         * also carries the product's recomputed sellable_qty and price range, so
         * the collapsed row refreshes without reloading the list.
         */
        async saveSkuEdit(row, skuRow) {
            if (!this.isAdmin || !row || !row.id || !skuRow) return;
            if (this.isSkuSaving(row, skuRow)) return;
            const draft = this.draftOf(row, skuRow);
            const payload = skuEditPayload(skuRow, draft);
            // Nothing typed, or only a WMS quantity that is never sent: treat ✓
            // as ✗ rather than firing a PATCH the BE answers 400 no_field.
            if (Object.keys(payload).length === 0) {
                this.cancelInlineEdit(row);
                return;
            }
            // Client validation mirrors the BE's (spec §2) so the common mistake
            // costs no round trip. The BE still decides.
            if (payload.price !== undefined && !(payload.price > 0)) {
                this.$notify({ type: "danger", message: this.$t("app_catalog.inline_price_invalid") });
                return;
            }
            if (payload.quantity !== undefined
                && (!(payload.quantity >= 0) || Math.floor(payload.quantity) !== payload.quantity)) {
                this.$notify({ type: "danger", message: this.$t("app_catalog.inline_qty_invalid") });
                return;
            }
            this.setSkuSaving(row, skuRow, true);
            try {
                const res = await this.patchSku({ id: row.id, sku_id: skuRow.id, payload: payload });
                const body = (res && res.data && res.data.data) || (res && res.data) || {};
                this.applySkuPatch(row, skuRow, body);
                this.$notify({ type: "success", message: this.$t("app_catalog.inline_saved") });
            } catch (err) {
                const key = inlineEditErrorKey(err);
                this.$notify({ type: "danger", message: this.$t(key) });
                // 409 quantity_wms_owned from a stale cache: the push took this
                // SKU over between the load and the save. Nothing was written —
                // flip the row locally so the input disappears instead of
                // inviting a second attempt that cannot succeed either.
                if (key === "app_catalog.inline_qty_wms_refused") {
                    this.markSkuFromWms(row, skuRow);
                }
                // The draft is KEPT: the operator may have a price to correct.
            } finally {
                this.setSkuSaving(row, skuRow, false);
            }
        },
        // Write the response onto the cached detail + the collapsed row. The SKU
        // objects inside `detailById` are what skuRowsOf renders, so mutating
        // them is what moves the sub-table.
        applySkuPatch(row, skuRow, body) {
            const sku = this.cachedSku(row, skuRow);
            if (sku) {
                if (body.price !== undefined && body.price !== null) this.$set(sku, "price", Number(body.price));
                if (body.quantity !== undefined && body.quantity !== null) this.$set(sku, "quantity", Number(body.quantity));
                if (body.stock_source !== undefined && body.stock_source !== null) {
                    this.$set(sku, "stock_source", String(body.stock_source));
                }
            }
            if (body.sellable_qty !== undefined && body.sellable_qty !== null) {
                this.$set(row, "sellable_qty", Number(body.sellable_qty));
            }
            if (body.price_min !== undefined && body.price_min !== null) {
                this.$set(row, "price_min", Number(body.price_min));
            }
            if (body.price_max !== undefined && body.price_max !== null) {
                this.$set(row, "price_max", Number(body.price_max));
            }
            // Re-seed this row's draft from what was actually stored, so a value
            // the BE rounded or clamped is the one on screen.
            const drafts = this.skuDraft[row.id];
            if (drafts && drafts[skuRow.key] && sku) {
                this.$set(drafts[skuRow.key], "price", Number(sku.price) || 0);
                this.$set(drafts[skuRow.key], "quantity", Number(sku.quantity) || 0);
            }
        },
        markSkuFromWms(row, skuRow) {
            const sku = this.cachedSku(row, skuRow);
            if (sku) this.$set(sku, "stock_source", "wms");
        },
        cachedSku(row, skuRow) {
            const detail = this.detailById[row && row.id];
            const skus = detail && Array.isArray(detail.skus) ? detail.skus : [];
            const wanted = Number(skuRow && skuRow.id) || 0;
            for (let i = 0; i < skus.length; i++) {
                if (skus[i] && Number(skus[i].id) === wanted && wanted > 0) return skus[i];
            }
            return null;
        },
        // The sub-table's thumbnail. One helper, @/utils/cdnImage, owns the rule
        // for both this page and PromotionBundleEdit.vue: a backend-resolved URL
        // is rendered byte for byte (its `?e=&s=&k=` signature is what gets it
        // past the CDN Worker), the legacy private-S3 origin is swapped for the
        // CDN origin with path+query intact, and a bare object key resolves to
        // "" instead of an unsigned URL this page cannot sign. Signed-image spec
        // docs/specs/2026-10-03-signed-image-url-design.md §6.4.
        thumbUrl(url) {
            return cdnImageUrl(url);
        },
        onRowCommand(cmd, row) {
            if (cmd === "edit") return this.goEdit(row.id);
            if (cmd === "preview") return this.openPreview(row);
            if (cmd === "delete") return this.confirmDelete(row);
        },
        // rel=noopener via window.open's third argument: the storefront is a
        // different origin and must not get a handle on the backoffice tab.
        openPreview(row) {
            const url = this.previewUrl(row);
            if (!url) return;
            window.open(url, "_blank", "noopener");
        },
        // ---- #583 WMS coverage ----
        // Both are one-liners into @/utils/wmsCoverage, where they are unit-tested:
        // the count must come from `wms_unmatched_count` (which counts past the
        // API's 20-SKU detail cap) and NOT from the names, because the API can
        // report a shortfall it cannot attribute to a specific warehouse code.
        wmsUnmatchedCountOf(row) {
            return wmsUnmatchedCount(row);
        },
        wmsUnmatchedTooltipOf(row) {
            return wmsUnmatchedTooltip(row, this.$t.bind(this));
        },
        formatPrice(v) {
            return Number(v || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        onPageChange(page) {
            this.params.page = page;
            this.loadList();
        },
        goCreate() {
            this.$router.push("/seller/products/new/edit");
        },
        editPath(id) {
            return productEditPath(id);
        },
        goEdit(id) {
            const path = productEditPath(id);
            if (path) this.$router.push(path);
        },
        // ---- large-image preview (owner 2026-10-15) ----
        // Opening = setting the prop; scroll lock, Esc/Tab, focus and every close
        // path live in ImagePreviewLightbox (bo-fn #475). `trigger` is the
        // thumbnail button, so focus goes back to it on close.
        openImagePreview(row, evt) {
            const p = imagePreviewOf(row);
            if (!p) return;
            this.imagePreview = previewOf(p.src, p.caption, (evt && evt.currentTarget) || null);
        },
        confirmDelete(row) {
            swal.fire({
                title: this.$t("app_catalog.delete_title"),
                text: this.$t("app_catalog.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("app_catalog.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
/* Image column (owner 2026-10-15). 48px square, cover-cropped so portrait and
   landscape uploads line up; the empty state keeps the same box so rows without
   a picture stay the same height. */
.product-list-thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 6px;
    object-fit: cover;
    vertical-align: middle;
    background: #f6f9fc;
    border: 1px solid #e9ecef;
}
.product-list-thumb--empty {
    color: #6c757d;
    background: #fff;
    border-style: dashed;
    font-size: 18px;
}
/* Header labels break at word boundaries only. element-ui sets
   `.el-table .cell { word-break: break-all }`, which let a Thai label wrap in
   the middle of a word; `normal` hands Thai to the browser's dictionary
   breaker and keeps English words whole. */
.product-list-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}
.product-list-head-lines {
    white-space: pre-line;
}
/* The thumbnail is a button (opens the large-image preview): no chrome of its
   own, the 48px image is the whole target. */
.product-list-thumb-btn {
    display: inline-block;
    padding: 0;
    border: 0;
    background: transparent;
    border-radius: 6px;
    line-height: 0;
    cursor: zoom-in;
}
.product-list-thumb-btn:hover .product-list-thumb {
    border-color: currentColor;
}
.product-list-thumb-btn:focus {
    outline: none;
}
.product-list-thumb-btn:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
}
/* The name is a link in the app's ACCENT colour, underline on hover only.
   Not the theme's `a` colour: in the Backoffice that is $primary #0c1026,
   which reads as body text. The seller app defines --seller-primary (brand
   red) on :root; the Backoffice does not, so it falls back to $info #0065c3
   (the navbar blue, 5.75:1 on white) — one byte-identical file, right accent
   in both apps. Inline so the 2-line clamp still applies to the text. */
.product-list-name-link {
    color: var(--seller-primary, #0065c3);
    font-weight: 600;
    text-decoration: none;
}
.product-list-name-link:hover,
.product-list-name-link:focus {
    color: var(--seller-primary, #0065c3);
    text-decoration: underline;
}
/* Thai name: wraps, at most two lines, full text on hover (title attribute). */
.product-list-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-word;
}
.product-list-wms {
    margin-top: 4px;
}
.product-list-nowrap {
    white-space: nowrap;
}
/* The incomplete badge is longer than the 132px the column holds
   ("ไม่สมบูรณ์ (ไม่มีชื่อตัวเลือก)"): let it wrap inside the cell instead of
   running into the switch column — only before the parenthesis: each half is a
   nowrap span (badgeLines), because element-ui's cells break-all and the Thai
   label otherwise split as "ตัวเ / ลือก" or "ตัว / เลือก" (render 2026-10-15). */
.product-list-incomplete .el-tag {
    white-space: normal;
    height: auto;
    line-height: 1.3;
    padding-top: 2px;
    padding-bottom: 2px;
}
</style>
<style>
/* product-list-tight: the theme gives every `.card .el-table` td/th 1.5rem of side
   padding, which left 24px of content in a 72px image column (the 48px thumbnail
   was clipped) and pushed the table past the card at 1366. 8px on the fixed
   columns only, at the specificity OrdersNew.vue's bon-tight uses to beat the
   theme rule. Unscoped because the td/th are rendered by el-table, not here. */
.card .el-table td.product-list-tight,
.card .el-table th.product-list-tight {
    padding-left: 8px;
    padding-right: 8px;
}
/* The WMS tooltip is one line per short SKU ("\n"-joined by wmsUnmatchedTooltip);
   popper-class lives outside this component, hence unscoped. */
.product-list-wms-tip {
    white-space: pre-line;
}
</style>
